import { Badge, BlockStack, Box, Button, Card, InlineStack, Link, Text } from '@shopify/polaris';
import { useEffect, useState } from 'react';
import { getRequest } from '../../shared/components/functions.jsx';
import { useAuthStore } from '../../context/useStore.js';
import { CardHeading } from '../../shared/components/cards/CardHeading.jsx';

const COLLAPSED_ROWS = 5;

function dueBadge(alert) {
  if (alert.daysLeft < 0) return <Badge tone="critical">{`${Math.abs(alert.daysLeft)}d late`}</Badge>;
  if (alert.daysLeft === 0) return <Badge tone="warning">today</Badge>;
  return <Badge tone="attention">{`in ${alert.daysLeft}d`}</Badge>;
}

function dueText(alert) {
  if (alert.dueDateSource === 'term') return `delivery term ${alert.deliveryTerm} (${alert.dueDate})`;
  if (alert.dueDateSource === 'confirmed') {
    return alert.requestedDate && alert.requestedDate !== alert.confirmedDate ? `confirmed ${alert.confirmedDate}, requested ${alert.requestedDate}` : `confirmed ${alert.confirmedDate}`;
  }
  return `requested ${alert.requestedDate}`;
}

function AlertList({ title, alerts, urlPrefix, numberPrefix }) {
  const [expanded, setExpanded] = useState(false);
  if (!alerts.length) return null;
  const visible = expanded ? alerts : alerts.slice(0, COLLAPSED_ROWS);
  const overdue = alerts.filter((alert) => alert.state === 'overdue').length;

  return (
    <BlockStack gap="200">
      <Text variant="headingSm" as="h3">
        {`${title}: ${overdue} overdue, ${alerts.length - overdue} due within a week`}
      </Text>
      {visible.map((alert) => (
        <InlineStack key={alert.id} gap="200" blockAlign="center" wrap>
          {dueBadge(alert)}
          <Link removeUnderline url={`${urlPrefix}/${alert.id}`}>{`${numberPrefix} ${alert.number}`}</Link>
          <Text>{alert.supplierName || '-'}</Text>
          {alert.reference ? <Text tone="subdued">{alert.reference}</Text> : null}
          <Text tone="subdued" variant="bodySm">
            {`${dueText(alert)}${alert.statusName ? ` · ${alert.statusName}` : ''}`}
          </Text>
        </InlineStack>
      ))}
      {alerts.length > COLLAPSED_ROWS ? (
        <InlineStack>
          <Button variant="plain" onClick={() => setExpanded((value) => !value)}>
            {expanded ? 'Show less' : `Show all ${alerts.length}`}
          </Button>
        </InlineStack>
      ) : null}
    </BlockStack>
  );
}

// Homepage: my purchase orders and inquiries whose delivery date is close or already passed (routes/delivery_alerts.js)
export default function DeliveryAlertsHomeCard() {
  const { user } = useAuthStore();
  const [alerts, setAlerts] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    getRequest('/api/delivery_alerts/mine').then((data) => {
      if (!cancelled && data && Array.isArray(data.purchases)) setAlerts(data);
    });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const purchases = alerts?.purchases || [];
  const inquiries = alerts?.inquiries || [];
  if (!purchases.length && !inquiries.length) return null;

  return (
    <Box paddingBlockEnd="300">
      <Card>
        <CardHeading title="My delivery terms" />
        <BlockStack gap="400">
          <AlertList title="Purchase orders" alerts={purchases} urlPrefix="/purchases" numberPrefix="PO" />
          <AlertList title="Inquiries" alerts={inquiries} urlPrefix="/inquiries" numberPrefix="Inquiry" />
        </BlockStack>
      </Card>
    </Box>
  );
}
